import { useFormikContext } from "formik";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useField } from "formik";
import { ReactNode } from "react";
import { VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";
import { inputVariants } from "./text-input";

export interface Option {
  value: string;
  label: string;
}

interface SelectInputProps extends VariantProps<typeof inputVariants> {
  options: Option[];
  label: string;
  placeholder: string;
  name: string;
  disabled?: boolean;
  children?: ReactNode;
  onChange?: (value: string) => void;
  className?: string;
}

export function SelectInput({
  options,
  label,
  placeholder,
  name,
  children,
  onChange,
  variant,
  height,
  radius,
  disabled = false,
  className = "",
}: SelectInputProps) {
  const [field, meta] = useField(name);
  const { setFieldValue, setFieldTouched } = useFormikContext();
  const hasError = meta.touched && meta.error;
  return (
    <div className={cn("w-full relative", className)}>
      <label htmlFor={name} className="block text-lg text-[#323232] mb-1">
        {label}
        {children}
      </label>

      <Select
        value={field.value}
        onValueChange={(value) => {
          setFieldValue(name, value);
          setFieldTouched(name, true);
          if (onChange) {
            onChange(value);
          }
        }}
        onOpenChange={(open) => {
          if (!open) {
            setFieldTouched(name, true);
          }
        }}
        disabled={disabled}
      >
        <SelectTrigger
          className={cn(
            inputVariants({ variant, height, radius }),
            `${
              hasError
                ? "!border-red-500 text-red-500 placeholder:!text-red-500 focus:ring-red-500"
                : ""
            }`,
          )}
        >
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent>
          <SelectGroup>
            <SelectLabel>{placeholder}</SelectLabel>
            {options.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectGroup>
        </SelectContent>
      </Select>
      <p
        className={`absolute -bottom-3 left-0 h-2 text-xs text-red-500 pl-3 mt-1 ${hasError ? "visible" : "invisible"}`}
      >
        {meta.error}
      </p>
    </div>
  );
}
